'use client';

import {Button} from '@lib/components/ui/button';
import ErrorConeImage from '@public/images/empty-states/errors/cone-error/error-cone.svg';
import Image from 'next/image';
import {useRouter} from 'next/navigation';
import type {MouseEvent} from 'react';

type Props = {
  handleReset?: (e?: MouseEvent<HTMLButtonElement>) => unknown;
  heading?: string;
  description?: string;
  button?: {text: string; handleBack: () => void};
};

const BaseError = ({handleReset, heading, description, button: backButton}: Props) => {
  const router = useRouter();
  const headingText = heading ?? `Something went wrong`;
  const descriptionText = description ?? `An unexpected error occurred. If the problem persists, contact support.`;
  const handleBack = backButton?.handleBack ?? (() => router.push('/'));
  const backText = backButton?.text ?? 'Back to Home';

  return (
    <div className="flex flex-col gap-3 items-center justify-center h-full">
      <Image src={ErrorConeImage} alt="error" width={150} height={152} priority />
      <div className="flex flex-col items-center text-center gap-1">
        <p className="text-header-sm font-semibold text-foreground" data-testid="error-heading">
          {headingText}
        </p>
        <p className="text-md text-muted-foreground" data-testid="error-description">
          {descriptionText}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button variant={handleReset ? 'ghost' : 'outline'} size="sm" onClick={handleBack}>
          {backText}
        </Button>

        {handleReset && (
          <Button variant="outline" size="sm" onClick={handleReset} data-testid="error-reload-button">
            Try again
          </Button>
        )}
      </div>
    </div>
  );
};

export default BaseError;
export type {Props as BaseErrorProps};
